<template>
  <div class="sales page page-with-table p-2">
    <page-header class="mb-2" :title="$t('Live Lot Performance')">
    </page-header>
    <div class="card content-wrapper p-2">
      <div class="live-lots-performance live-lots-table table-component bg-white">
        <div v-if="saleAggregateDetails" class="d-flex align-items-center mb-2 pr-1" style="gap: 5px; min-height: 60px">
          <amount-viewer :title="$t('Total Over Reserve')"
                         :amount="`${aggregateSummary.total_count_over_reserve} ${$t('lots')} / ${$options.filters.asCurrency(aggregateSummary.total_amount_over_reserve, saleAggregateDetails.currency.symbol)}`"
                         text-class="text-info"
                         style="flex: 1"
                         v-animate-fade="0.1"
                         :icon="'money-bill'"
                         :realtime="true"
                         :loading="false"/>
          <amount-viewer :title="$t('Total Under Reserve')"
                         :amount="`${aggregateSummary.total_count_bellow_reserve} ${$t('lots')} / ${$options.filters.asCurrency(aggregateSummary.total_amount_bellow_reserve, saleAggregateDetails.currency.symbol)}`"
                         text-class="text-info"
                         style="flex: 1"
                         v-animate-fade="0.1"
                         :icon="'money-bill'"
                         :realtime="true"
                         :loading="false"/>
          <amount-viewer :title="$t('Total Highest Bids')"
                         :amount="`${$options.filters.asCurrency(aggregateSummary.total_highest_bids)}`"
                         text-class="text-info"
                         style="flex: 1"
                         v-animate-fade="0.1"
                         :icon="'money-bill'"
                         :realtime="true"
                         :loading="false"/>
          <amount-viewer :title="$t('Total Max Bids')"
                         :amount="`${$options.filters.asCurrency(aggregateSummary.total_max_bids)}`"
                         text-class="text-info"
                         style="flex: 1"
                         v-animate-fade="0.1"
                         :icon="'money-bill'"
                         :realtime="true"
                         :loading="false"/>
          <amount-viewer :title="$t('Unrealized Max Bids')"
                         :icon="'money-bill'"
                         text-class="text-danger"
                         :currency="saleAggregateDetails.currency.symbol"
                         style="flex: 1"
                         v-animate-fade="0.5"
                         :amount="{[saleAggregateDetails.currency.code]: aggregateSummary.unrealized_max_bids}"
                         :realtime="true"
                         :loading="false"/>
          <div>
            <button @click="loadSaleDetails" v-b-modal.auction-aggregate-details
                    class="btn btn-secondary d-inline-flex align-items-center">
              <font-awesome-icon icon="chevron-circle-down" class="icon mr-2"/>
              {{ $t('More') }}
            </button>
          </div>
        </div>
        <artisio-table :fields="tableFields"
                       :busy="liveLots.loading"
                       :items="computedLots"
                       show-toggle-columns
                       :enable-simple-search="false"
                       :enable-front-end-pagination="true"
                       :pagination="pagination"
                       :limit-variants="[5, 50, 100, 200]"
                       @update-toggle-columns="updateToggleColumns"
                       :sticky-header="true"
                       :extra-data="filteredToggleColumns"
                       primary-key="uuid"
                       filter-toggle-string="llp-lots-table"
                       @onSortChange="onSortChange"
                       @on-column-save="updateToggleColumns">
          <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                    v-for="dynamicField in toggleColumns.dynamicFields">
            <span :key="`${item.uuid}-${dynamicField}`"
                  v-html="$options.filters.asDynamicField($options.filters.masterLanguage(item.dynamic_fields)[dynamicField], field.type, field.choices)">
            </span>
          </template>

          <template v-slot:header-buttons>
            <div class="header-buttons">
              <!--                  <b-input v-model="filterContent" :placeholder="$t('Type to Filter Lots')"/>-->
              <div class="llp-sale-select-dropdown">
                <input-widget type="select2" v-if="salesLoaded"
                              :model="model"
                              attribute="savedSaleUUID"
                              :label="false"
                              :options="{placeholder: $t('Please Select Sale')}"
                              @select2Select="onSaleChange"
                              display-field="${item.text}"
                              :items="saleOptions"/>
              </div>
              <b-input-group class="llp-paddle-number-input">
                <b-form-input v-if="model.savedSaleUUID"
                              v-model="paddleNumber"
                              :placeholder="$t('Paddle Number')"
                              @keyup.prevent.enter="onPaddleNoChange"
                              @blur="onPaddleNoChange"
                              v-b-tooltip.hover
                              :title="bidderError"
                              :state="bidderError === null ? null : !bidderError"/>
              </b-input-group>

              <customer-email-address-info v-if="bidder" :customer="bidder.customer"/>
            </div>
          </template>
          <template v-slot:search-form-placeholder>
            <search-collapse table-id="llp-lots-table">
              <div class="card mb-2">
                <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
                  <b-form class="search-form" @submit.prevent="onSearch">
                    <div class="d-flex">
                      <div class="row align-items-center">

                        <div class="input-wrapper">
                          <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="lot_no"/>
                        </div>
                        <div class="input-wrapper">
                          <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="title"/>
                        </div>
                        <div class="input-wrapper">
                          <bidder-select-input-widget :model="searchModel" attribute="bidder_uuid"
                                                      @select="onBidderSelect"
                                                      :auction-uuid="savedSaleUUID" is-search
                                                      value-field="uuid"
                                                      :label="false" :options="{placeholder: $t('Bidder')}"/>
                        </div>
                      </div>
                      <div class="search-btn-wrapper">
                        <b-button variant="primary" @click.prevent="onResetClick">
                          <font-awesome-icon icon="undo" class="icon"/>
                          {{ $t('Reset') }}
                        </b-button>
                        <b-button type="submit" variant="primary" class="ml-2">
                          <font-awesome-icon icon="search" class="icon"/>
                          {{ $t('Search') }}
                        </b-button>
                      </div>
                    </div>
                  </b-form>
                </div>
              </div>
            </search-collapse>
          </template>
          <template slot="cell(sale_no)" slot-scope="{item: lot}" class="col-sale_no">
            <router-link v-if="!enableContinuousAuction" v-permission="'view_auction'"
                         :to="{ name: 'sales.view', params: { id: lot.auction_uuid }}" target="_blank">
              {{ lot.sale_no }}
            </router-link>
            <span v-else-if="enableContinuousAuction">
                  {{ lot.sale_no }}
                </span>
          </template>
          <template slot="cell(sale_title)" slot-scope="{item: lot}">
            <div v-b-tooltip.hover
                 :title="$options.filters.masterLanguage(lot.sale_title)">

              <router-link v-if="!enableContinuousAuction"
                           v-permission="'view_auction'"
                           :to="{ name: 'sales.view', params: { id: lot.auction_uuid }}">
                {{ lot.sale_title | masterLanguage }}
              </router-link>
              <span v-else-if="enableContinuousAuction"
                    v-b-tooltip.hover
                    :title="$options.filters.masterLanguage(lot.sale_title)">
                    {{ lot.sale_title | masterLanguage }}
                  </span>
            </div>
          </template>
          <template slot="cell(lot_no)" slot-scope="{item: lot}" class="col-lot_no">
            <router-link v-if="$hasPermission('view_inventoryitem')"
                         :to="{ name: 'inventory.view', params: { id: lot.inventory_uuid }}" target="_blank">
              {{ lot.lot_no }}
            </router-link>
          </template>
          <template slot="cell(thumbnail)" slot-scope="{item: lot}" class="col-thumbnail">
            <b-img v-if="lot.thumbnail" :src="lot.thumbnail.url" style=""/>
            <b-img v-else src="@/assets/no-image.jpg"/>
          </template>
          <template slot="cell(title)" slot-scope="{item: lot}" class="col-title">
            <div v-b-tooltip.hover
                 :title="$options.filters.striptags(lot.title)">
              <router-link v-if="$hasPermission('view_inventoryitem')"
                           :to="{ name: 'inventory.view', params: { id: lot.inventory_uuid }}" target="_blank">
                {{ lot.title | striptags }}
              </router-link>
            </div>
          </template>
          <template slot="cell(auction_start_date)" slot-scope="{item: lot}">
            {{ lot.auction_start_date | toDateTime }}
          </template>
          <template slot="cell(auction_end_date)" slot-scope="{item: lot}">
            {{ lot.auction_end_date | toDateTime }}
          </template>
          <template slot="cell(lot_status)" slot-scope="{item: lot}" class="col-lot_status">
                <span class="badge" :class="getClassObjectForLotStatus(lot)">
                  {{ (lot.status === 'lotted' ? 'live' : lot.status) | toUpperCaseFirst }}
                </span>
          </template>
          <template slot="cell(current_bid)" slot-scope="{item: lot}">
            {{ lot.current_bid | asCurrency(lot.currency.symbol) }}
            <b-progress v-if="lot.current_bid && lot.reserve" :value="lot.current_bid" :max="lot.reserve"
                        :variant="reserveMet(lot) ? 'success' : 'danger'"></b-progress>
          </template>
          <template slot="cell(highest_pre_bid)" slot-scope="{item: lot}">
            <template v-if="lot.highest_pre_bid">
              {{ lot.highest_pre_bid.amount | asCurrency(lot.currency.symbol) }}
              <br>
              <a href="javascript:void(0)" @click="showCustomerModal(lot)">
                {{ lot.highest_pre_bid.bidder_display_name }}
              </a>
            </template>
          </template>
          <template slot="cell(paddle_number)" slot-scope="{item: lot}">
            <template v-if="lot.bidder  && lot.bidder.uuid">
              <a href="javascript:void(0)" @click="showCustomerModal(lot)">
                {{ lot.bidder ? lot.bidder.paddle_number : '' }}
              </a>
            </template>
          </template>
          <template slot="cell(bidder)" slot-scope="{item: lot}" class="col-bidder">
            <template v-if="lot.bidder  && lot.bidder.uuid">
              <a href="javascript:void(0)" @click="showCustomerModal(lot)">
                {{ lot.bidder.display_name }}
              </a>
              - {{ lot.bidder.email }}
            </template>
          </template>
          <template #cell(number_of_bids)="{item: lot, toggleDetails}">
            <a v-if="lot.number_of_bids > 0" href="javascript:void(0)" @click="toggleDetails">
              {{ lot.number_of_bids }} {{ lot.number_of_bids === 1 ? $t('bid') : $t('bids') }}
            </a>
            <span v-else>
                  0 {{ $t('bids') }}
                </span>
          </template>
          <template #row-details="{item: lot}">
            <llp-bids-table :lot="lot"/>
          </template>
          <template slot="cell(end_date)" slot-scope="{item: lot}" class="col-end_date">
            <count-down-timer :start_date="lot.start_date" :end_date="lot.end_date" :label="false"/>
          </template>
          <template slot="cell(start_price)" slot-scope="{item: lot}" class="col-start_price">
            {{ lot.start_price | asCurrency(lot.currency.symbol) }}
          </template>
          <template slot="cell(increment)" slot-scope="{item: lot}" class="col-increment">
            <template v-if="getLotIncrements(lot).length > 1">
              <table class="live-lots-increments-table">
                <tbody>
                <tr>
                  <td>{{ getLotIncrements(lot)[0].increment | asCurrency(lot.currency.symbol) }}</td>
                  <td>
                    {{ $t('Up to') }} {{ getLotIncrements(lot)[0].max | asCurrency(lot.currency.symbol) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <a href="javascript:void(0)"
                       :id="`increments-popover-${lot.uuid}`"
                       @click="showHideIncrementsPopover(lot)">
                      +{{ getLotIncrements(lot).length - 1 }} {{ $t('more') }}
                    </a>
                  </td>
                </tr>
                </tbody>
              </table>
            </template>
            <template v-else>
              <div class="d-flex align-items-start">
                <cell-editing-input :value="getLotIncrements(lot)[0].increment" type="number" min="1"
                                    @ok="(val) => onSingleIncrementSave(lot, val)"/>
                <a v-if="lot.increment_calculation_source !== 'reserve'" href="javascript:void(0)"
                   v-b-tooltip.hover
                   :title="$t('Add new increment tier')"
                   :id="`increments-popover-${lot.uuid}`"
                   class="btn btn-sm btn-primary ml-2"
                   @click="showHideIncrementsPopover(lot)">
                  <font-awesome-icon icon="plus"/>
                </a>
              </div>
            </template>
            <increments-popover :target="`increments-popover-${lot.uuid}`"
                                :show="showIncrementsPopover[lot.uuid]"
                                :increments="getLotIncrements(lot)"
                                @cancel="onIncrementCancelClick(lot)"
                                @ok="(increments) => onIncrementSave(lot, increments)"/>
          </template>
          <template slot="cell(reserve)" slot-scope="{item: lot}">
            <!--          <template v-if="lot.editingReserve">-->
            <cell-editing-input :value="lot.reserve" @ok="(val) => onReserveSave(lot, val)" type="number" min="1"/>
            <!--          </template>-->
            <!--          <template v-else>-->
            <!--            {{ lot.reserve | asCurrency(lot.currency.symbol) }}-->
            <!--          </template>-->
          </template>
          <template slot="cell(bid_create)" slot-scope="{item: lot}" class="col-bid_create">
            <b-input-group size="sm" class="next-increment-bid-amount-input-group">
              <b-form-input :placeholder="$t('Bid Amount')"
                            :value="getNextAmountByIncrements(lot)"
                            @input="nextBidAmountChange($event, lot)"/>
              <b-input-group-append v-b-tooltip.hover
                                    :title="getPlaceBidButtonTooltipText(lot)">
                <b-button @click="onCreateBidClick(lot)"
                          variant="primary"
                          :disabled="disableBidButtons.includes(lot.uuid) || isCreateBidDisabled(lot) || !!getPlaceBidButtonTooltipText(lot)">
                  <font-awesome-icon icon="gavel" class="mr-1"/>
                  <span class="font-lg">{{ $t('Place Bid') }}</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </template>
          <template slot="cell(actions)" slot-scope="{item: lot}" class="col-actions">
            <button class="btn btn-sm btn-primary mr-1" @click="showLotModal(lot)">
              <font-awesome-icon icon="edit"/>
            </button>
            <b-dropdown variant="primary" size="sm" no-caret>
              <template #button-content>
                <font-awesome-icon icon="cog"/>
              </template>
              <b-dropdown-item @click="onWithdrawClick(lot)" v-permission="'auction_results_auction'">
                {{ $t('Withdraw') }}
              </b-dropdown-item>
              <b-dropdown-item @click="onHideClick(lot)">
                {{ lot.hide_lot ? $t('Show on sale') : $t('Hide on sale') }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <!--            </tr>-->
          <!--            </tbody>-->
        </artisio-table>
      </div>
    </div>
    <b-modal id="auction-aggregate-details" :title="$t('Auction Aggregate Details')" :ok-title="$t('Ok')"
             :cancel-title="$t('Cancel')">
      <table v-if="saleAggregateDetails" class="table table-sm table-striped sale-aggregates-table">
        <tbody>
        <tr>
          <th>
            {{ $t('No. of Bidders') }}
          </th>
          <td>{{ saleAggregateDetails.total_bidders }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Approved Bidders') }}
          </th>
          <td>{{ saleAggregateDetails.approved_bidders }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Pending Bidders') }}
          </th>
          <td>{{ saleAggregateDetails.pending_bidders }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Rejected Bidders') }}
          </th>
          <td>{{ saleAggregateDetails.rejected_bidders }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Number of Lots') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_lots }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Sold') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.sold_lots }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Unsold') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.unsold_lots }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Over Reserve') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_count_over_reserve }} {{ $t('lots') }} /
            {{ aggregateSummary.total_amount_over_reserve | asCurrency(saleAggregateDetails.currency.symbol) }}
          </td>
        </tr>
        <!--        <tr>-->
        <!--          <th>-->
        <!--            {{ $t('Total Over Reserve (Amount)') }}-->
        <!--            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>-->
        <!--          </th>-->
        <!--          <td>{{ aggregateSummary.total_amount_over_reserve | asCurrency(saleAggregateDetails.currency.symbol)}}</td>-->
        <!--        </tr>-->
        <tr>
          <th>
            {{ $t('Total Under Reserve') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_count_bellow_reserve }} {{ $t('lots') }} /
            {{ aggregateSummary.total_amount_bellow_reserve | asCurrency(saleAggregateDetails.currency.symbol) }}
          </td>
        </tr>
        <!--        <tr>-->
        <!--          <th>-->
        <!--            {{ $t('Total Under Reserve (Amount)') }}-->
        <!--            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>-->
        <!--          </th>-->
        <!--          <td>{{ aggregateSummary.total_amount_bellow_reserve | asCurrency(saleAggregateDetails.currency.symbol)}}</td>-->
        <!--        </tr>-->
        <tr>
          <th>
            {{ $t('Total Low Estimate') }}
          </th>
          <td>{{ saleAggregateDetails.total_low | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total High Estimate') }}
          </th>
          <td>{{ saleAggregateDetails.total_high | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Reserve') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_reserve | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Highest Bids') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_highest_bids | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Total Max Bids') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.total_max_bids | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        <tr>
          <th>
            {{ $t('Unrealized Max Bids') }}
            <div class="blinking-dot" v-b-tooltip.hover.top="$t('Data updates in Realtime')"></div>
          </th>
          <td>{{ aggregateSummary.unrealized_max_bids | asCurrency(saleAggregateDetails.currency.symbol) }}</td>
        </tr>
        </tbody>
      </table>
    </b-modal>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from 'vuex'
  import i18n from "../../shared/i18n";
  import {
    SOURCE_CUSTOMER_TABLE,
    SOURCE_LOTS_TABLE,
    WS_LLP_AUCTION_UPDATE,
    WS_LLP_LOT_UPDATE,
    WS_LLP_LOT_STARTED,
    WS_LLP_AUCTION_ENDED,
    WS_LLP_BID_CREATED,
    WS_LLP_AUCTION_SUBSCRIBED,
  } from "../constants";
  import CellEditingInput from "./CellEditingInput";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import _ from "lodash";
  import IncrementTiers from "../../core/components/increment-tiers/IncrementTiers";
  import IncrementsPopover from "./IncrementsPopover";
  import BidsTable from "../bids/BidsTable";
  import LlpBidsTable from "./LlpBidsTable";
  import CountDownTimer from "../../core/components/common/CountDownTimer";
  import LotsService from "../lots/LotsService";
  import IncrementsService from "../sales/view/increments/IncrementsService";
  import Spinner from "../../core/components/common/Spinner";
  import AddNewButton from "../../core/components/common/AddNewButton";
  import PageHeader from "../../core/components/page-header/PageHeader";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget.vue";
  import LiveLotsPerformanceSearch from "@/modules/live-lots-performance/LiveLotsPerformance.model";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import BiddersService from "@/modules/bidders/BiddersService";
  import SalesService from "@/modules/sales/SalesService";
  import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo.vue";
  import BidModel from "@/modules/bids/Bid.model";
  import BidSearch from "@/modules/bids/BidSearch.vue";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse.vue";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget.vue";
  import LLPLotsSearchModel from "@/modules/live-lots-performance/LLPLotsSearch.model";
  import AmountViewer from "@/core/components/common/AmountViewer.vue";

  const {mapState, mapActions, mapMutations} = createNamespacedHelpers('liveLotsPerformance');
  const {mapActions: mapCustomerActions, mapMutations: mapCustomerMutations} = createNamespacedHelpers('customer')
  const {mapActions: mapLotActions} = createNamespacedHelpers('lots');
  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');
  const {mapActions: mapBidsActions} = createNamespacedHelpers('bids');

  export default {
    name: "LiveLotsPerformance",
    components: {
      AmountViewer,
      BidderSelectInputWidget,
      SearchCollapse,
      BidSearch,
      CustomerEmailAddressInfo,
      InputWidget,
      PageHeader,
      AddNewButton,
      Spinner,
      CountDownTimer,
      LlpBidsTable,
      BidsTable,
      IncrementsPopover,
      IncrementTiers,
      ArtisioTable,
      CellEditingInput,
      SaleSelectInputWidget,
    },
    props: {
      loading: Boolean
    },
    data: () => ({
      excludeColumns: [],
      incrementsPopoverStatus: {},
      model: new LiveLotsPerformanceSearch(),
      saleOptions: [],
      sales: [],
      salesLoaded: false,
      socketListeners: {},
      // filterContent: '',
      paddleNumber: '',
      bidder: null,
      bidderError: null,
      bidModel: new BidModel(),
      showIncrementsPopover: {},
      pagination: {
        limit: 50,
        page: 1,
        total: 1,
      },
      disableBidButtons: [], // lot uuids for which the button should be disabled
      savedSaleUUID: null,
      sortBy: null,
      sortDesc: false,
      searchModel: new LLPLotsSearchModel(),
      searchData: {},
      saleAggregateDetails: null
    }),
    computed: {
      ...mapStateGlobal({
        enableContinuousAuction: state => state.enableContinuousAuction,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        isSocketConnected: state => state.isSocketConnected,
      }),
      ...mapState(['liveLots', 'liveBids']),
      ...mapState({
        lots: state => state.liveLots.data,
        toggleColumns: state => state.toggleColumns,
        // savedSaleUUID: state => state.sales.savedSaleUUID,
        savedPaddleNumber: state => state.sales.savedPaddleNumber,
      }),
      filteredToggleColumns() {
        return {
          ...this.toggleColumns,
          fields: this.toggleColumns.fields.filter(field => {
            if (field.key === 'bid_create') return !this.enableContinuousAuction
            return true
          })
        }
      },
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => {
            if (field.key === 'bid_create') return !this.enableContinuousAuction
            return !this.excludeColumns.includes(field.key) && field.isVisible
          })
      },
      computedLots() {
        // const q = this.filterContent.toLowerCase();
        let lots = this.lots;
        // if (q) {
        lots = this.lots
          .filter(l => {

            return Object.keys(this.searchData).every(key => {
              const searchValue = this.searchData[key];
              if (searchValue === '' || searchValue === undefined || searchValue === null) {
                return true; // Skip this search parameter
              }
              // If the item has the property and it matches the search criteria
              return (
                (key === 'lot_no' && l[key] === this.searchData[key]) ||
                (key === 'bidder_uuid' && l.bidder && l.bidder.uuid === this.searchData[key]) ||
                (key === 'title' && l[key].toLowerCase().includes(this.searchData[key].toLowerCase()))
              );
            });

            // if (Object.keys(this.searchData).length) {
            //   for (let key in this.searchData) {
            //     const value = this.searchData[key]
            //     if (l[key].includes(value)) {
            //       return true;
            //     }
            //   }
            //   return false;
            // }

            // return l.sale_no.toLowerCase().includes(q)
            //   || this.$options.filters.masterLanguage(l.sale_title).toLowerCase().includes(q)
            //   || l.lot_no.toLowerCase().includes(q)
            //   || this.$options.filters.striptags(l.title).toLowerCase().includes(q)
            //   || (l.bidder && l.bidder.paddle_number.includes(q))
            //   || (l.bidder && l.bidder.display_name.toLowerCase().includes(q))
            //   || (l.bidder && l.bidder.email.toLowerCase().includes(q))
          })
        // }
        lots.forEach(lot => {
          if (lot.hide_lot)
            lot['_rowVariant'] = 'lot-hidden';
          else
            delete lot['_rowVariant'];
        });
        // console.log(this.sortBy, this.sortDesc, lots)
        return lots
          .sort((l1, l2) => {
            if (this.sortBy === 'lot_no') {
              // Regular expression to separate the numeric part and the suffix
              const lotNoRegex = /^(\d+)([A-Za-z]*)$/;

              // Extract numeric part and suffix for both lot_no values
              const [, aNum, aSuffix] = l1.lot_no.match(lotNoRegex) || [];
              const [, bNum, bSuffix] = l2.lot_no.match(lotNoRegex) || [];

              // Convert numeric parts to numbers for comparison
              const numA = parseInt(aNum, 10);
              const numB = parseInt(bNum, 10);

              // Adjust comparison for numeric parts based on sortDesc
              const numComparison = this.sortDesc ? numB - numA : numA - numB;
              if (numComparison !== 0) return numComparison;

              // If numeric parts are equal, compare the suffixes
              if (aSuffix === bSuffix) return 0;
              if (!aSuffix) return this.sortDesc ? 1 : -1; // Adjust based on this.sortDesc
              if (!bSuffix) return this.sortDesc ? -1 : 1;  // Adjust based on this.sortDesc

              // Adjust alphabetical comparison for suffixes based on this.sortDesc
              return this.sortDesc ? bSuffix.localeCompare(aSuffix) : aSuffix.localeCompare(bSuffix);
            }

            if (this.sortBy === 'paddle_number') {
              const val1 = l1.bidder ? l1.bidder.paddle_number : null;
              const val2 = l2.bidder ? l2.bidder.paddle_number : null;

              if (val1 < val2) {
                return this.sortDesc ? 1 : -1;
              }
              if (val1 > val2) {
                return this.sortDesc ? -1 : 1;
              }
            }

            if (this.sortBy === 'highest_pre_bid') {
              const val1 = l1.highest_pre_bid ? l1.highest_pre_bid.amount : null;
              const val2 = l2.highest_pre_bid ? l2.highest_pre_bid.amount : null;

              if (val1 < val2) {
                return this.sortDesc ? 1 : -1;
              }
              if (val1 > val2) {
                return this.sortDesc ? -1 : 1;
              }
            }
            // console.log(this.sortBy, l1[this.sortBy], l2[this.sortBy])
            if (l1[this.sortBy] < l2[this.sortBy]) {
              return this.sortDesc ? 1 : -1;
            }
            if (l1[this.sortBy] > l2[this.sortBy]) {
              return this.sortDesc ? -1 : 1;
            }

            if (['sold', 'unsold'].includes(l1.status) && !['sold', 'unsold'].includes(l2.status)) {
              return 1
            } else if (['sold', 'unsold'].includes(l2.status) && !['sold', 'unsold'].includes(l1.status)) {
              return -1;
            }
            const tmp1 = new Date(l1.end_date)
            const tmp2 = new Date(l2.end_date)
            return tmp1.getTime() - tmp2.getTime();
          });
      },
      computedLotLength() {
        return this.computedLots.length
      },
      bidAmounts() {
        const bidAmounts = {};
        for (let lot of this.computedLots) {
          bidAmounts[lot.uuid] = this.getNextAmountByIncrements(lot)
        }
        return bidAmounts
      },
      currentSale() {
        return this.sales.find(sale => sale.uuid === this.savedSaleUUID)
      },
      aggregateSummary() {
        return this.lots.reduce((accum, current) => {
          // console.log((current.highest_pre_bid ? current.highest_pre_bid.amount : 0))
          const highest_pre_bid = (current.highest_pre_bid ? current.highest_pre_bid.amount : 0)
          const current_bid = (current.current_bid || 0)
          const reserve = (current.reserve || 0)
          accum.unrealized_max_bids += Math.max(highest_pre_bid - current_bid, 0);
          accum.total_highest_bids += current_bid;
          accum.total_max_bids += highest_pre_bid;
          accum.total_reserve += reserve;
          accum.total_count_over_reserve += (current_bid && current_bid >= reserve) ? 1 : 0
          accum.total_amount_over_reserve += (current_bid && current_bid >= reserve) ? current_bid : 0
          accum.total_count_bellow_reserve += (!current_bid || current_bid < reserve) ? 1 : 0
          accum.total_amount_bellow_reserve += (!current_bid || current_bid < reserve) ? current_bid : 0
          accum.sold_lots += current.status === 'sold' ? 1 : 0;
          accum.unsold_lots += current.status === 'unsold' ? 1 : 0;
          accum.total_lots += current.status !== 'withdrawn' ? 1 : 0;
          return accum;
        }, {
          unrealized_max_bids: 0,
          total_highest_bids: 0,
          total_max_bids: 0,
          total_reserve: 0,
          total_count_over_reserve: 0,
          total_amount_over_reserve: 0,
          total_count_bellow_reserve: 0,
          total_amount_bellow_reserve: 0,
          sold_lots: 0,
          unsold_lots: 0,
          total_lots: 0
        })
      }
    },
    methods: {
      ...mapActions(['getLiveLots', 'updateToggleColumns', 'getLiveBids']),
      ...mapCustomerActions(['showModal', 'getModalObject']),
      ...mapLotActions(['showReservationModal', 'withdrawItem']),
      ...mapLotActions({
        getModalLotObject: 'getModalObject'
      }),
      ...mapMutations([
        'updateLlpLots',
        'updateLlpAuction',
        'updateSavedPaddleNumber',
        'appendLlpLots',
        'removeAuctionLots',
        'removeLot'
      ]),
      ...mapBidsActions({
        showBidActionsModal: 'showModal',
        createBid: 'createBid'
      }),
      isCreateBidDisabled(lot) {
        const isTimed = lot.sale_type === 'timed';
        const isLotted = lot.status === 'lotted'
        return !(isTimed && isLotted && this.hasArtisioTimedProvider);
      },
      showCustomerModal(lot) {
        this.showModal();
        this.getModalObject({uuid: lot.bidder.customer_uuid, source: SOURCE_CUSTOMER_TABLE})
      },
      async onSaleChange(sale) {
        this.$router.push({
          params: {auctionUuid: sale.uuid}
        });
      },
      showLotModal(lot) {
        this.showReservationModal({
          source: SOURCE_LOTS_TABLE
        });
        this.getModalLotObject({uuid: lot.reservation_uuid, source: SOURCE_LOTS_TABLE});
      },
      reserveMet(lot) {
        return lot.current_bid && lot.reserve && lot.current_bid >= lot.reserve;
      },
      onIncrementCancelClick(lot) {
        this.showIncrementsPopover[lot.uuid] = false;
      },
      onIncrementSave(lot, increments) {
        this.showIncrementsPopover[lot.uuid] = false;
        this.$confirm(
          this.$i18n.t("Are you sure you want to update the increments on lot No {lot_no}?", {lot_no: lot.lot_no}),
          this.$i18n.t('Increments were successfully updated on lot No {lot_no}', {lot_no: lot.sale_no}),
          (lot) => this.updateIncrements(increments, lot),
          lot
        )
        // .then(() => this.updateSaleInfo())
      },
      onSingleIncrementSave(lot, val) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to update the increments on lot No {lot_no}?", {lot_no: lot.lot_no}),
          this.$i18n.t('Increment was successfully updated on lot No {lot_no}', {lot_no: lot.sale_no}),
          (lot) => this.updateSingleIncrement(val, lot),
          lot
        )
        // .then(() => this.updateSaleInfo())
      },
      onReserveSave(lot, val) {
        LotsService.partialUpdateLot(lot.reservation_uuid, {reserve: val})
          .then((res) => {
            if (res.success) {
              this.$toast('success', this.$i18n.t(`Lot {lot} reserve was successfully updated`, {lot: lot.lot_no}));
              lot.reserve = val;
            } else {
              this.$toast('danger', res.body.reserve[0]);
            }
          })
      },
      cancelIncrementEditing(lot) {
        this.incrementsPopoverStatus[lot.uuid] = false;
      },
      onWithdrawClick(lot) {
        this.$confirm(
          this.$i18n.t("The action cannot be undone. Are you sure you want to withdraw lot {lot_no} from the sale {sale}?", {lot: lot.lot_no}),
          this.$i18n.t('Lot {lot} was successfully withdrawn from sale {sale}', {lot: lot.lot_no, sale: lot.sale_no}),
          this.withdrawLot,
          lot
        );
      },
      onHideClick(lot) {
        let message;
        let successMessage;
        if (!lot.hide_lot) {
          message = this.$i18n.t("Are you sure you want to hide following item?", {lot: lot.lot_no});
          successMessage = this.$i18n.t('Lot {lot} was successfully hidden on sale {sale}', {
            lot: lot.lot_no,
            sale: lot.sale_no
          });
        } else {
          message = this.$i18n.t("You are about to display the lot on published sale. Are you sure?", {lot: lot.lot_no});
          successMessage = this.$i18n.t('Lot {lot} was successfully displayed on sale {sale}', {
            lot: lot.lot_no,
            sale: lot.sale_no
          });
        }
        this.$confirm(message, successMessage, this.hideLot, lot);
      },
      withdrawLot(lot) {
        return this.withdrawItem(lot.reservation_uuid)
          .then(response => {
            return response;
          })
      },
      hideLot(lot) {
        lot.hide_lot = !lot.hide_lot;
        return LotsService.partialUpdateLot(lot.reservation_uuid, {hide_lot: lot.hide_lot})
      },
      updateIncrements(increments, lot) {
        increments = this.prepareIncrements(increments, lot)
        return IncrementsService.postLotIncrements(lot.uuid, increments)
          .then((response) => {
            lot.lot_increment = increments
            lot.next_bid = this.getNextAmountByIncrements(lot)
            return response;
          })
      },
      updateSingleIncrement(val, lot) {
        const increments = this.prepareIncrements(this.getLotIncrements(lot), lot)
        increments[0].increment = parseInt(val);
        return IncrementsService.postLotIncrements(lot.uuid, increments)
          .then((response) => {
            lot.lot_increment = increments
            lot.next_bid = this.getNextAmountByIncrements(lot)
            return response;
          })
      },
      prepareIncrements(increments, lot) {
        // [{"max":null,"increment":"5000","lot_uuid":"c0bb0a4a-5225-4f77-b4e0-a7146af143dd"}]
        return increments.map(inc => {
          inc.lot_uuid = lot.uuid;
          return {...inc}
        })
      },
      getClassObjectForLotStatus(lot) {
        return {
          'badge-danger': lot.status === 'lotted',
          'badge-success': lot.status === 'sold',
          'badge-secondary': lot.status === 'unsold',
        }
      },
      showHideIncrementsPopover(lot) {
        // this.showIncrementsPopover[lot.uuid] = !this.showIncrementsPopover[lot.uuid];
        this.$set(this.showIncrementsPopover, lot.uuid, !this.showIncrementsPopover[lot.uuid])
      },
      updateWebsocketBids(lotsData) {
        for (let i in lotsData) {
          let lot = this.liveLots.data.find(lot => lot.uuid === lotsData[i].uuid);
          if (!lot) {
            continue;
          }
          let existingBidsIndex = this.liveBids.findIndex(el => el.lot_uuid === lotsData[i].uuid);

          // check if number of existing bids + number of new bids equals number of total bids to detect lost bids
          if (lot.number_of_bids + lotsData[i].bids.length !== lotsData[i].bids_count) {
            // fetch bids via ajax here
            this.getLiveBids(lot);
            // TODO or maybe first check if bids table is open?
          } else {
            lot.number_of_bids = lotsData[i].bids_count;
            lot.current_bid = lotsData[i].bids[0].amount;
            lot.bidder = {
              display_name: lotsData[i].bids[0].bidder.customer.display_name,
              email: lotsData[i].bids[0].bidder.customer.email,
              paddle_number: lotsData[i].bids[0].bidder.paddle_number,
              uuid: lotsData[i].bids[0].bidder.uuid
            };
            lot.next_bid = this.getNextAmountByIncrements(lot)
            // As numbers match, we just append new bids, no need for request
            // But first check if bids table is open
            if (existingBidsIndex !== -1) {
              this.liveBids[existingBidsIndex].bids = [...lotsData[i].bids, ...this.liveBids[existingBidsIndex].bids];
            }
          }
        }
      },
      updateSaleInfo() {
        this.model = new LiveLotsPerformanceSearch({savedSaleUUID: this.savedSaleUUID})
        this.getLiveLots(this.savedSaleUUID)
          .then(lots => {
            this.pagination.total = lots.length
          })
        this.isSocketConnected && this.$ws.sendObj({auction_uuid: [this.savedSaleUUID]}, WS_LLP_AUCTION_SUBSCRIBED)
      },
      getNextAmountByIncrements(lot) {
        let currentBid = lot.current_bid || 0

        if (currentBid || !lot.start_price) {
          const increment = this.getIncrementByCurrentBid(currentBid, [...this.getLotIncrements(lot)])
          if (!increment) return '';
          return currentBid + increment;
        }
        return lot.start_price
      },

      getIncrementByCurrentBid(currentBid, increments) {
        increments = increments.sort((a, b) => a.min - b.min)
          .map(inc => ({
            "up_to": inc.max,
            "increment": inc.increment,
          }));

        let increment = increments.find(inc => inc.up_to && inc.up_to > currentBid);
        if (!increment) {
          // Find last increment without max
          increment = increments.find(inc => !inc.up_to);
        }
        return parseInt(increment.increment);
      },
      onPaddleNoChange() {
        this.updateSavedPaddleNumber(this.paddleNumber)
      },
      async getBidderInfo() {
        this.bidder = null;
        this.bidderError = null;

        if (!this.paddleNumber || !this.model.savedSaleUUID) {
          return;
        }

        const {body, success} = await BiddersService.getBidders({
          exact_paddle_number: this.paddleNumber,
          auction_uuid: this.savedSaleUUID
        }, 'customer');

        if (success && body && body.results && body.results.length) {
          this.bidder = body.results[0]
        } else {
          this.bidderError = this.$t("Bidder was not found");
        }
      },
      async onCreateBidClick(lot) {

        if (!this.bidder || !this.model.savedSaleUUID) {
          return false
        }

        const model = new BidModel();
        model.auction_uuid = lot.auction_uuid
        model.lot_uuid = lot.uuid;
        model.lot_no = lot.lot_no;
        model.bidder_uuid = this.bidder.uuid;
        model.amount = lot.next_bid !== undefined ? lot.next_bid : this.bidAmounts[lot.uuid];

        this.disableBidButtons = [...this.disableBidButtons, lot.uuid];

        const response = await this.createBid({...model.toJSON()});
        this.disableBidButtons = this.disableBidButtons.filter(uuid => uuid !== lot.uuid);

        if (response.success) {
          lot.next_bid = this.getNextAmountByIncrements(lot)
          this.$toast('success', this.$t('Bid was successfully created'));
        } else {
          this.$errorDialog(response.body.non_field_errors[0])
        }
      },
      getPlaceBidButtonTooltipText(lot) {
        if (!this.paddleNumber) {
          return this.$t(`Please enter bidder's paddle number.`)
        }

        if (!this.bidder) {
          return this.$t('Please enter correct paddle number for approved bidder.')
        }

        if (!this.bidAmounts[lot.uuid]) {
          return this.$t('Please valid bid amount')
        }

        return '';
      },
      nextBidAmountChange(value, lot) {
        lot.next_bid = parseFloat(value)
      },
      getLotIncrements(lot) {
        if (lot.lot_increment && lot.lot_increment.length) {
          return lot.lot_increment;
        }

        if (!this.currentSale || !this.currentSale.increment_calculation_source || this.currentSale.increment_calculation_source === 'bid') {
          return lot.increment.map(inc => {
            delete inc.uuid
            return inc
          }).sort((a, b) => parseFloat(a.min) - parseInt(b.min));
        }

        // Create new array of because sorting the lot.increment directly was causing infinite loop in browser's console
        // and I don't have enough time to find it out why and fix it.
        const increments = [...lot.increment].sort((a, b) => parseFloat(a.min) - parseInt(b.min));

        const checkAgainst = lot.reserve || lot.start_price;
        if (!checkAgainst) {
          const increment = increments[0]
          return [{
            'increment': parseFloat(increment.increment),
            'max': null,
            'increment_range': increment.increment_range
          }];
        }
        if (this.currentSale.increment_calculation_source === 'reserve') {
          for (let i in increments) {
            const increment = increments[i];

            if (!increment.max || increment.max > checkAgainst) {
              delete increment.uuid
              return [{
                'increment': parseFloat(increment.increment),
                'max': null,
                'increment_range': increment.increment_range
              }];
            }
          }
        }

        return null
      },
      getLotsForContinuous() {
        this.getLiveLots(this.savedSaleUUID)
          .then(lots => {
            this.pagination.total = lots.length
            const auctionUuids = lots.map(l => l.auction_uuid)

            this.isSocketConnected && this.$ws.sendObj({auction_uuid: auctionUuids}, WS_LLP_AUCTION_SUBSCRIBED)
          })
      },
      onSortChange({sortBy, sortDesc}) {
        this.sortBy = sortBy;
        this.sortDesc = sortDesc
      },
      onSearch() {
        this.searchData = {
          lot_no: this.searchModel.lot_no,
          title: this.searchModel.title,
          bidder_uuid: this.searchModel.bidder_uuid
        }
      },
      onResetClick() {
        this.searchData = {}
        this.searchModel = new LLPLotsSearchModel()
      },
      onBidderSelect(bidder) {
        this.searchModel.bidder_uuid = bidder.uuid
      },
      async loadSaleDetails() {
        const {body, success} = await SalesService.getSaleForLLP(this.savedSaleUUID)
        if (success) {
          this.saleAggregateDetails = body;
        }
      }
    },
    watch: {
      'savedSaleUUID': {
        handler() {
          if (!this.enableContinuousAuction && this.savedSaleUUID) {
            this.updateSaleInfo()
            this.getBidderInfo()
            this.loadSaleDetails();
          }

        },
        immediate: true
      },
      '$route.params.auctionUuid': {
        handler() {
          this.savedSaleUUID = this.$route.params.auctionUuid
        },
        immediate: true
      },
      savedPaddleNumber: {
        handler() {
          this.paddleNumber = this.savedPaddleNumber
          this.getBidderInfo()
        },
        immediate: true
      },
      'isSocketConnected': {
        async handler() {
          if (!this.enableContinuousAuction) {
            this.updateSaleInfo()
          } else {
            this.getLotsForContinuous()
          }
        }
      },
      computedLotLength() {
        console.log("Changed ", this.computedLotLength)
        this.pagination.total = this.computedLotLength
      }
    },
    beforeMount() {

      if (this.enableContinuousAuction) {
        this.getLotsForContinuous()
      }

      this.socketListeners[`llp.${WS_LLP_LOT_STARTED}`] = this.appendLlpLots;
      this.socketListeners[`llp.${WS_LLP_LOT_UPDATE}`] = this.updateLlpLots;
      this.socketListeners[`llp.${WS_LLP_AUCTION_UPDATE}`] = this.updateLlpAuction;
      this.socketListeners[`llp.${WS_LLP_AUCTION_ENDED}`] = this.removeAuctionLots;
      this.socketListeners[`llp.${WS_LLP_BID_CREATED}`] = this.updateWebsocketBids;
    },
    async mounted() {

      if (!this.enableContinuousAuction) {
        const {body, success} = await SalesService.getSalesForLLPLots({limit: 200})
        if (success && body && body.results) {
          this.sales = body.results;
          this.saleOptions = body.results.map(sale => ({
            uuid: sale.uuid,
            text: `${sale.sale_no} - ${this.$options.filters.masterLanguage(sale.title)}`
          }))
          this.salesLoaded = true
        }
      }

      for (const key in this.socketListeners) {
        this.$ws.listen(key, this.socketListeners[key]);
      }
    },
    beforeDestroy() {
      for (const key in this.socketListeners) {
        this.$ws.remove(key, this.socketListeners[key]);
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../core/scss/variables";

  /deep/ .table-lot-hidden {
    background-color: #ECECEC;
  }

  /deep/ .form-group {
    min-width: 200px;
    margin-bottom: 0;
  }

  /deep/ .customer-email-address-info {
    display: flex;
    align-items: center;
  }

  .llp-sale-select-dropdown {
    min-width: 360px;
    max-width: 360px;
  }

  .llp-paddle-number-input {
    input {
      max-width: 140px;
      background-image: none;
    }
  }

  .live-lots-table {
    overflow-x: auto;

    .artisio-table {

      /deep/ {
        .table-responsive {
          padding-bottom: 25px;
        }

        .b-table-has-details {
          //background-color: lighten($primary, 45%);
          > td:first-child {
            border-left: 5px solid $primary
          }
        }

        .b-table-details {
          //background-color: lighten($primary, 45%);
          > td:first-child {
            border-left: 5px solid $primary
          }
        }

        th,
        .col-actions,
        .col-title,
        .col-sale_title,
        .col-bidder {
          white-space: nowrap;
        }

        .col-title {
          max-width: 200px;

          > div {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .col-sale_title {
          max-width: 200px;

          > div {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .live-lots-increments-table {
          td {
            border: none;
            padding-top: 0;
            padding-bottom: 0;
            padding-left: 0;
          }
        }

        .col-current_bid {
          position: relative;

          .progress {
            position: absolute;
            width: 100%;
            left: 0;
            right: 0;
            bottom: 0;
            height: 5px;
            border-radius: 0;
          }
        }

        .col-reserve {
          min-width: 90px;
        }

        .col-increment {
          min-width: 124px;
        }

        .col-end_date {
          min-width: 100px;
        }

        .col-thumbnail {
          > img {
            width: 100px;
          }
        }

      }
    }
  }

  .next-increment-bid-amount-input-group {
    display: flex;
    flex-wrap: nowrap;

    input {
      font-size: 13.6px;
      min-width: 60px;
      padding: 2px;
      height: auto;
      color: black;
    }
  }

  .sale-aggregates-table {
    th {
      position: relative;
    }
  }

  .blinking-dot {
    display: inline-block;
    position: relative;
    top: -5px;
    //right: 5px;
    width: 7px; /* Diameter */
    height: 7px; /* Diameter */
    background-color: #00ad3f;
    border-radius: 50%; /* Makes the dot round */
    animation: blink-animation 1.5s infinite; /* Blinking effect */
  }

  @keyframes blink-animation {
    0% {
      background-color: #00ad3f;
    }
    50% {
      background-color: transparent;
    }
    100% {
      background-color: #00ad3f;
    }
  }
</style>
<style lang="scss">
  .llp-increments-popover {
    width: 300px;

    .popover-body {
      padding: 0;
    }
  }

  .header-buttons {
    display: flex;
    flex-direction: row;
    gap: 0.5em;
    flex-wrap: nowrap;
    flex: 1;
  }

  .continuous-auctions-lots-list {
    height: calc(100%);
    overflow-y: auto;
    position: relative;
  }
</style>
